import React, { useState } from 'react';
import { CalendarClock, Flag, Info, Mail } from 'lucide-react';

import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'shadcn/components/ui/Dialog';
import { HARDCODED_COMPANY_SOURCES } from 'shared/companySource';
import { CompanySourceBadge } from 'shared/components/companySource/CompanySourceBadge';

import { CrossDockIconBadge, getStatusBadgeClassName } from './PurchaseOrderStatusCell';

const STATUS_LEGEND_ORDER = [
    'PLACED',
    'RECEIVED',
    'PAID',
    'CANCELLED',
    'OPEN',
    'IN PROGRESS',
    'QC COMPLETE',
    'CONSUMED',
];

const SCHEDULED_DELIVERY_LEGEND_ITEMS = [
    {
        definition: 'A delivery time other than midnight is set, indicating an appointment is scheduled.',
        iconClass: 'text-emerald-600',
    },
    {
        definition: 'A delivery time of midnight indicates no appointment is scheduled.',
        iconClass: 'text-amber-500',
    },
    {
        definition:
            'Late Delivery: No appointment is scheduled (midnight delivery time) and the delivery date is within 7 days.',
        iconClass: 'text-red-600',
    },
];

/**
 * Button that opens a modal with the combined table legend (Status + Scheduled Delivery).
 */
export function PurchaseOrderLegendButton() {
    const [open, setOpen] = useState(false);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 [&_svg]:size-5"
                    aria-label="Open table legend"
                >
                    <Info className="h-5 w-5" aria-hidden="true" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Table legend</DialogTitle>
                </DialogHeader>
                <div className="flex gap-3">
                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium">All Statuses:</span>
                        {STATUS_LEGEND_ORDER.map((status) => (
                            <Badge key={status} variant="outline" className={getStatusBadgeClassName(status)}>
                                {status}
                            </Badge>
                        ))}
                    </div>
                    <div className="border-l border-border pl-3">
                        <span className="text-xs font-medium">Icons:</span>
                        <div className="flex flex-col gap-2">
                            <div className="flex items-start gap-2">
                                <span className="mt-0.5 shrink-0">
                                    <Badge
                                        variant="outline"
                                        className="bg-orange-100 text-orange-900 border-orange-200 hover:bg-orange-100 hover:text-orange-900 flex h-fit w-fit items-center gap-1"
                                    >
                                        <CrossDockIconBadge />
                                    </Badge>
                                </span>
                                <span className="text-xs">PO is bound for a cross dock.</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="mt-0.5 shrink-0">
                                    <Flag className="h-4 w-4 text-red-600" aria-hidden="true" />
                                </span>
                                <span className="text-xs">
                                    PO is either manually marked as Priority or is considered Late Delivery.
                                </span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="mt-0.5 shrink-0">
                                    <Mail className="h-4 w-4 text-red-600" aria-hidden="true" />
                                </span>
                                <span className="text-xs">This purchase order has not been emailed to the vendor</span>
                            </div>
                            {HARDCODED_COMPANY_SOURCES.map((source) => (
                                <div key={source.id} className="flex items-start gap-2">
                                    <span className="mt-0.5 shrink-0">
                                        <CompanySourceBadge companySourceIds={[source.id]} />
                                    </span>
                                    <span className="text-xs">
                                        Purchase order includes line items for {source.displayName}.
                                    </span>
                                </div>
                            ))}
                            {SCHEDULED_DELIVERY_LEGEND_ITEMS.map(({ definition, iconClass }) => (
                                <div key={iconClass} className="flex items-start gap-2">
                                    <span className="mt-0.5 shrink-0">
                                        <CalendarClock className={`h-4 w-4 ${iconClass}`} aria-hidden="true" />
                                    </span>
                                    <span className="text-xs">{definition}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
